import React, { useContext, useMemo } from 'react';
import { PostScheduleViewContext } from '../PostSchedule.controller';
import classes from './ThirdLevelNavigation.module.scss';
import { ButtonGroup } from '../../../../components/containers/ButtonGroup';
import { Button } from 'components/actions/Button';
import { ButtonColorsEnum } from 'constants/button-colors';
import { IconsEnum } from '../../../../constants/icons';
import classNames from 'classnames';
import { ButtonMenu } from '../../../../components/actions/ButtonMenu';
import { PostScheduleViewProps } from '../PostSchedule.view';
import { PostQueueCountResult, QueueFilterPostStatus, QueueFilterPostTiming } from '../../../../models/graphql';
import { format, isAfter } from 'date-fns';
import { PostScheduleFilterType } from '../../../../models/post';
import { useWorkspaceTime } from '../../../../hooks/useWorkspaceTime';
import { PostScheduleFilterMenu } from '../PostScheduleFilterMenu';
import { FilterDropdown } from 'components/containers/FilterDropdown';
import { Tooltip } from '../../../../components/actions/Tooltip';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';
import { OnboardingContext } from '../../../Onboarding/states/OnboardingProvider';
import { OnboardingStaticContent } from '../../../Onboarding/constants/content';

export type ThirdNavLeftButtonsProps = PostScheduleViewProps & {
  context: PostScheduleViewContext;
  setContext: (context: PostScheduleViewContext) => void;
  setShowFilterMenu: (show: boolean) => void;
  setDateRange: (amount: number) => void;
  isLoading: boolean;
  queueCounts: PostQueueCountResult;
};

export type ThirdNavRightButtonsProps = {
  context: PostScheduleViewContext;
  deleteLabel: string;
  hasPosts: boolean;
  isDateSetFilter: boolean;
  queueFilters: PostScheduleFilterType;
  actOnAllPosts: (callback: CallableFunction) => void;
  isQueuePaused: boolean;
  deleteAction: (show: boolean) => void;
  pauseAction: (show: boolean) => void;
  shuffleAction: (show: boolean) => void;
  isLoading: boolean;
};

export type ThirdNavLibraryFiltersProps = PostScheduleViewProps & {
  setShowFilterMenu: (show: boolean) => void;
  queueCounts: PostQueueCountResult;
};

export const ThirdNavLibraryFilters = (props: ThirdNavLibraryFiltersProps): JSX.Element => {
  return (
    <div className={'grid-x show-for-small-only'}>
      <div className={'show-for-small-only'}>
        <Button
          type={'button'}
          variant={'hollow'}
          size={'medium'}
          color={ButtonColorsEnum.primary}
          icon={{ side: 'left', value: IconsEnum.filter }}
          action={() => props.setShowFilterMenu(true)}
          disabled={false}>
          Filters
        </Button>
      </div>
    </div>
  );
};

export const ThirdNavLeftButtons = (props: ThirdNavLeftButtonsProps): JSX.Element => {
  const { startDate, endDate } = props;
  const dateRangeLabel = useMemo(() => {
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`;
  }, [endDate, startDate]);
  const { convertUtcTimeToWorkspaceTime } = useWorkspaceTime();
  const disablePrevButton = isAfter(convertUtcTimeToWorkspaceTime(), startDate);
  const viewButtonGroupClasses = classNames('show-for-medium', classes.LeftButtonGroupContainer, {
    [classes.LeftButtonGroupContainerWeek]: props.context === PostScheduleViewContext.WEEK
  });
  const dateValueClasses = classNames(classes.DateValue, 'show-for-large');

  const filterMenu = useMemo(
    () => (
      <PostScheduleFilterMenu
        filters={props.filters}
        context={props.context}
        dispatchFilters={props.dispatchFilters}
        categories={props.categories}
        socialProfiles={props.socialProfiles}
        formInstance={props.formInstance}
        onSubmit={props.onSubmit}
        setSearchTerm={props.setSearchTerm}
        queueCounts={props.queueCounts}
        isDropdown={true}
      />
    ),
    [props]
  );

  return (
    <>
      <div className={'grid-x'}>
        <div className={'show-for-small-only'}>
          <Button
            type={'button'}
            variant={'hollow'}
            size={'medium'}
            color={ButtonColorsEnum.primary}
            icon={{ side: 'left', value: IconsEnum.filter }}
            action={() => props.setShowFilterMenu(true)}
            disabled={props.isLoading}>
            Filters
          </Button>
        </div>
        {props.context === PostScheduleViewContext.WEEK && <FilterDropdown>{filterMenu}</FilterDropdown>}
        <div className={viewButtonGroupClasses}>
          <ButtonGroup size={'medium'}>
            <Button
              type={'button'}
              variant={props.context === PostScheduleViewContext.LIST ? 'solid' : 'hollow'}
              size={'medium'}
              color={ButtonColorsEnum.primary}
              action={() => props.setContext(PostScheduleViewContext.LIST)}
              disabled={props.isLoading}>
              List
            </Button>
            <Button
              type={'button'}
              variant={props.context === PostScheduleViewContext.WEEK ? 'solid' : 'hollow'}
              size={'medium'}
              color={ButtonColorsEnum.primary}
              action={() => {
                props.dispatchFilters({
                  type: 'status',
                  payload: { data: QueueFilterPostStatus.SCHEDULED, fetchPosts: true }
                });
                props.setContext(PostScheduleViewContext.WEEK);
              }}
              disabled={props.isLoading}>
              Week
            </Button>
          </ButtonGroup>
        </div>
        {props.context === PostScheduleViewContext.WEEK && (
          <>
            <div className={viewButtonGroupClasses}>
              <ButtonGroup size={'medium'}>
                <Button
                  type={'button'}
                  variant={'hollow'}
                  size={'medium'}
                  color={ButtonColorsEnum.primary}
                  icon={{ side: 'center', value: IconsEnum.arrowLeft }}
                  action={() => props.setDateRange(-1)}
                  disabled={props.isLoading || disablePrevButton}
                />
                <Button
                  type={'button'}
                  variant={'hollow'}
                  size={'medium'}
                  color={ButtonColorsEnum.primary}
                  action={() => props.setDateRange(0)}
                  disabled={props.isLoading}>
                  Today
                </Button>
                <Button
                  type={'button'}
                  variant={'hollow'}
                  size={'medium'}
                  color={ButtonColorsEnum.primary}
                  icon={{ side: 'center', value: IconsEnum.arrowRight }}
                  action={() => props.setDateRange(1)}
                  disabled={props.isLoading}
                />
              </ButtonGroup>
            </div>
            <div className={dateValueClasses}>{dateRangeLabel}</div>
          </>
        )}
      </div>
    </>
  );
};

export const ThirdNavRightButtons = (props: ThirdNavRightButtonsProps) => {
  const { isTemporallySuspended } = useSubscriptionStatus();
  const { showOnboarding, state: onboardingState } = useContext(OnboardingContext);
  const { version } = onboardingState;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.posts;
  return (
    <>
      <div className={'grid-x show-for-tablet-portrait'}>
        {props.queueFilters.status === QueueFilterPostStatus.SCHEDULED && (
          <>
            {props.context !== PostScheduleViewContext.WEEK && (
              <div className={classes.RightButtonGroupContainer}>
                <Button
                  type={'button'}
                  variant={'hollow'}
                  size={'medium'}
                  color={ButtonColorsEnum.primary}
                  action={() => props.shuffleAction(true)}
                  disabled={
                    props.isLoading ||
                    !props.hasPosts ||
                    props.isDateSetFilter ||
                    props.queueFilters.timing === QueueFilterPostTiming.RECURRING
                  }>
                  Shuffle
                </Button>
              </div>
            )}
            <div className={classes.RightButtonGroupContainer}>
              <Button
                type={'button'}
                variant={'hollow'}
                size={'medium'}
                color={ButtonColorsEnum.primary}
                action={() => props.pauseAction(!props.isLoading || props.isQueuePaused)}
                disabled={props.isLoading || isTemporallySuspended}>
                {props.isQueuePaused ? <>Unpause All</> : <>Pause All</>}
              </Button>
            </div>
          </>
        )}
        {props.queueFilters.status !== QueueFilterPostStatus.PAST && props.context !== PostScheduleViewContext.WEEK && (
          <>
            <div className={classes.RightButtonGroupContainer} id={'thirdNavQueueDeleteAllButton'}>
              <Button
                type={'button'}
                variant={'hollow'}
                size={'medium'}
                color={ButtonColorsEnum.primary}
                action={() => props.deleteAction(true)}
                disabled={props.isLoading || !props.hasPosts || (showOnboarding && disableDelete)}>
                {props.deleteLabel} All
              </Button>
              {showOnboarding && disableDelete ? (
                <Tooltip target={'#thirdNavQueueDeleteAllButton'} type={'regular'} position={'bottom'} pointer={true}>
                  You will be able to delete these demo posts later.
                </Tooltip>
              ) : null}
            </div>
          </>
        )}
      </div>
      <div className={'hide-for-tablet-portrait'}>
        <ButtonMenu
          isSquareButton={false}
          variantOnOpen={'solid'}
          side={'right'}
          icon={IconsEnum.threeDot}
          size={'medium'}
          variant={'hollow'}
          listItems={
            props.context !== PostScheduleViewContext.WEEK && !props.isQueuePaused
              ? [
                  { label: 'Shuffle', action: props.shuffleAction, disabled: !props.hasPosts },
                  {
                    label: props.isQueuePaused ? 'Unpause All' : 'Pause All',
                    action: () => props.pauseAction(!props.isQueuePaused)
                  },
                  {
                    label: `${props.deleteLabel} All`,
                    action: () => props.deleteAction(true),
                    disabled: !props.hasPosts
                  }
                ]
              : [
                  {
                    label: props.isQueuePaused ? 'Unpause All' : 'Pause All',
                    action: () => props.pauseAction(!props.isQueuePaused)
                  }
                ]
          }
        />
      </div>
    </>
  );
};
